import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Copy, X } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";

const KEY = "rathri_browser_warned";

const detect = () => {
  const ua = navigator.userAgent || "";
  if (/SamsungBrowser/i.test(ua)) return "Samsung Internet";
  if (/MiuiBrowser/i.test(ua)) return "Mi Browser";
  if (/VivoBrowser/i.test(ua)) return "Vivo Browser";
  if (/HeyTapBrowser|OppoBrowser/i.test(ua)) return "Oppo Browser";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "Facebook";
  if (/; wv\)/i.test(ua)) return "In-App Browser";
  return null;
};

const copyLink = async () => {
  const url = window.location.href;
  try { await navigator.clipboard.writeText(url); return true; } catch (e) { /* fallback below */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
};

export const BrowserGate = () => {
  const [browser, setBrowser] = useState(null);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(KEY)) return;
      const b = detect();
      if (b) setBrowser(b);
    } catch (e) { /* storage blocked — still show warning */ const b = detect(); if (b) setBrowser(b); }
  }, []);
  const close = () => { try { sessionStorage.setItem(KEY, "1"); } catch (e) { /* ignore */ } setBrowser(null); };
  if (!browser) return null;
  const copy = async () => {
    const ok = await copyLink();
    if (ok) toast.success("ലിങ്ക് കോപ്പി ചെയ്തു ✓ — ഇനി Google Chrome / Safari തുറന്ന് പേസ്റ്റ് ചെയ്യുക", { duration: 8000 });
    else toast.error("കോപ്പി ചെയ്യാൻ കഴിഞ്ഞില്ല — മുകളിലെ അഡ്രസ് ബാറിൽ നിന്ന് ലിങ്ക് സെലക്ട് ചെയ്ത് കോപ്പി ചെയ്യുക", { duration: 8000 });
  };
  return createPortal(
    <div data-testid="browser-warning-modal" className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={close} />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl p-6 space-y-4 fade-up border-2 border-amber-400/70">
        <button type="button" data-testid="browser-warning-close" onClick={close} className="absolute top-3 right-3 btn-ghost w-8 h-8 rounded-full inline-flex items-center justify-center"><X size={14} /></button>
        <Brand size="sm" tagline={false} />
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-2xl bg-amber-500 text-white inline-flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30"><AlertTriangle size={22} /></span>
          <div>
            <p className="font-display text-base md:text-lg font-extrabold">ബ്രൗസർ മാറ്റുക</p>
            <p data-testid="browser-warning-detected" className="text-xs text-[var(--muted-text)]">നിങ്ങൾ ഉപയോഗിക്കുന്നത്: {browser}</p>
          </div>
        </div>
        <p data-testid="browser-warning-text" className="text-sm font-semibold leading-relaxed">മികച്ച ഓഡിയോ, വീഡിയോ കോൾ അനുഭവത്തിനായി ദയവായി ഈ ലിങ്ക് Google Chrome അല്ലെങ്കിൽ Safari ബ്രൗസറിൽ ഓപ്പൺ ചെയ്യുക.</p>
        <button type="button" data-testid="browser-warning-copy-button" onClick={copy} className="w-full h-12 rounded-full bg-[var(--accent-c)] text-white font-bold text-sm inline-flex items-center justify-center gap-2 shadow-lg transition-opacity hover:opacity-90 active:scale-95"><Copy size={16} /> ലിങ്ക് കോപ്പി ചെയ്യുക</button>
        <p className="text-[10px] text-[var(--muted-text)] text-center">കോപ്പി ചെയ്ത ലിങ്ക് Chrome / Safari അഡ്രസ് ബാറിൽ പേസ്റ്റ് ചെയ്ത് ഓപ്പൺ ചെയ്യുക</p>
        <button type="button" data-testid="browser-warning-continue-button" onClick={close} className="w-full text-xs text-[var(--muted-text)]">ഈ ബ്രൗസറിൽ തന്നെ തുടരുക</button>
      </div>
    </div>,
    document.body
  );
};
